import React, { useRef, useState, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';
import { useNavigate } from 'react-router-dom';
import * as faceapi from 'face-api.js';
import {
  Button,
  Typography,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  AspectRatio,
  Alert,
  IconButton,
} from '@mui/joy';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import Close from '@mui/icons-material/Close';
import Warning from '@mui/icons-material/Warning';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraIcon from '@mui/icons-material/Camera';
import ForwardIcon from '@mui/icons-material/Forward';
import api from '../../api/apiClient';
import useAppStore from '../../store/appStore';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';
import { handleError } from '../../utils/error-handling';

const MODEL_URL = process.env.PUBLIC_URL + '/models'; // Update with the path to your face-api models

const videoConstraintsFront = {
  facingMode: 'user',
};

const videoConstraintsBack = {
  facingMode: { exact: 'environment' },
};

const FaceVerificationPage = () => {
  const { t } = useTranslation();
  const webcamRef = useRef(null);
  const { setLoading, isRTL, loading } = useAppStore();
  const [isCameraOpen, setIsCameraOpen] = useState(true);
  const [facingMode, setFacingMode] = useState('user'); // "user" or "environment"
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const navigate = useNavigate();

  /**
   * Load face-api models on mount
   */
  useEffect(() => {
    const loadModels = async () => {
      setLoading(true);
      try {
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      } catch (err) {
        console.error('Error loading face-api models:', err);
      } finally {
        setLoading(false);
      }
    };
    loadModels();
  }, []);

  /**
   * Continuously check for a face in the video feed
   * once models are loaded and the camera is open
   */
  useEffect(() => {
    if (!loading && isCameraOpen && webcamRef.current) {
      const intervalId = setInterval(() => {
        detectFace();
      }, 500); // Check every 500ms
      return () => clearInterval(intervalId);
    }
  }, [loading, isCameraOpen]);

  /**
   * Detect face using face-api.js
   */
  const detectFace = async () => {
    if (
      webcamRef.current &&
      webcamRef.current.video &&
      webcamRef.current.video.readyState === 4
    ) {
      console.log('detecting face');
      const video = webcamRef.current.video;
      const detection = await faceapi.detectSingleFace(
        video,
        new faceapi.TinyFaceDetectorOptions()
      );

      if (detection) {
        // Get the dimensions of the video element
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;

        // Calculate the center and radius of our circular mask
        const centerX = videoWidth / 2;
        const centerY = videoHeight / 2;
        const radius = Math.min(videoWidth, videoHeight) / 2.2; // Slightly smaller than the mask

        // Get face bounds
        const faceX = detection.box.x + detection.box.width / 2;
        const faceY = detection.box.y + detection.box.height / 2;
        const faceRadius =
          Math.max(detection.box.width, detection.box.height) / 2;

        // Check if face is centered and fully inside the circle
        const distanceFromCenter = Math.sqrt(
          Math.pow(faceX - centerX, 2) + Math.pow(faceY - centerY, 2)
        );

        if (distanceFromCenter + faceRadius <= radius) {
          // Face is completely inside the circle
          const screenshot = webcamRef.current.getScreenshot();
          setIsCameraOpen(false);
          setPhoto(screenshot);
        }
      }
    }
  };

  /**
   * Create new function for handling upload
   */
  const handleUpload = async () => {
    if (!photo) return;

    setLoading(true);
    const resp = await fetch(photo);
    const blob = await resp.blob();
    const formData = new FormData();
    formData.append('faceImage', blob, 'face.jpg');

    try {
      const response = await api.post('/auth/face', formData);
      console.log(response.data);

      if (response.data.success) {
        navigate('/card-possession');
      }
      if (response.data.tr)
        handleError('error', t(`faceVerification.${response.data.tr}`));
    } catch (error) {
      console.log('heree', error?.response?.data?.tr);
      console.log(t(`faceVerification.${error?.response?.data?.tr}`));
      setError(true);
      handleError(
        'error',
        error?.response?.data?.tr
          ? t(`faceVerification.${error?.response?.data?.tr}`)
          : error.message
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Retake photo
   */
  const handleRetake = () => {
    setPhoto(null);
    setIsCameraOpen(true);
    setError(false);
    setShowAlert(true);
  };

  /**
   * Switch front/back camera
   */
  const switchCamera = () => {
    setFacingMode(prevMode => (prevMode === 'user' ? 'environment' : 'user'));
  };

  function handleContinue() {
    setShowWarningDialog(true);
  }

  async function handleConfirmContinue() {
    console.log('confirming continue');
    try {
      await api.post('/auth/no-face');
    } catch (error) {
      handleError('error', error?.response?.data?.tr || error);
    }
    setShowWarningDialog(false);
    navigate('/card-possession');
  }

  return (
    <div className="flex flex-col items-center mt-6">
      {/* Instructions */}
      {isCameraOpen && (
        <Typography
          level="body-lg"
          textAlign="center"
          fontStyle="italic"
          className="text-center px-4"
        >
          {t('faceVerification.centerFaceInstruction')}
        </Typography>
      )}

      {/* Camera & Circular Mask */}
      {isCameraOpen && (
        <div className="relative inline-block mt-8">
          <Webcam
            ref={webcamRef}
            audio={false}
            mirrored
            screenshotFormat="image/jpeg"
            videoConstraints={
              facingMode === 'user'
                ? videoConstraintsFront
                : videoConstraintsBack
            }
            className="rounded-full w-80 h-80 object-cover border-2 border-black"
          />
          {isCameraOpen && false && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Typography className="text-white text-8xl">
                {'helllo'}
              </Typography>
            </div>
          )}

          {/* Circular Mask Border (for tailwind, you can also do border-8 etc.) */}
          <div
            className="
              pointer-events-none 
              absolute top-0 left-0 
              w-80 h-80 
              rounded-full 
              border-4 border-white 
              border-opacity-80
            "
          />
        </div>
      )}

      {/* Display captured photo */}
      {photo && (
        <div className="mt-4">
          <img src={photo} alt="Captured" className="w-80 h-auto rounded" />
        </div>
      )}

      {/* Show both Upload and Retake buttons when photo is taken */}
      {photo && !error && (
        <div className="mt-4 flex gap-4">
          <Button
            variant="soft"
            color="neutral"
            onClick={handleRetake}
            startDecorator={<CameraIcon />}
          >
            {t('faceVerification.retake')}
          </Button>
          <Button
            variant="soft"
            color="success"
            onClick={handleUpload}
            startDecorator={<CloudUploadIcon />}
          >
            {t('faceVerification.uploadPhoto')}
          </Button>
        </div>
      )}

      {/* Keep the error message and retake button for error cases */}
      {error && (
        <div className="flex flex-col items-center mt-4 mb-4">
          {showAlert && (
            <Alert
              variant="soft"
              color="danger"
              invertedColors
              startDecorator={
                <AspectRatio
                  object-fit="contain"
                  variant="solid"
                  ratio="1"
                  sx={{
                    minWidth: 40,
                    borderRadius: '50%',
                  }}
                >
                  <div>
                    <Warning fontSize="xl2" />
                  </div>
                </AspectRatio>
              }
              endDecorator={
                <IconButton
                  variant="plain"
                  sx={{
                    '--IconButton-size': '32px',
                    transform: 'translate(0.5rem, -0.5rem)',
                  }}
                  onClick={() => setShowAlert(false)}
                >
                  <Close />
                </IconButton>
              }
              sx={{ alignItems: 'flex-start', overflow: 'hidden' }}
            >
              <div>
                <Typography
                  level="body-sm"
                  color="danger"
                  className="text-start"
                >
                  {t('faceVerification.error1')}
                </Typography>
                <Typography
                  level="body-sm"
                  color="danger"
                  className="text-start"
                >
                  {t('faceVerification.error2')}
                </Typography>
                <Typography
                  level="body-sm"
                  color="danger"
                  className="text-start font-semibold"
                >
                  {t('faceVerification.error3')}
                </Typography>
              </div>
            </Alert>
          )}
          <Stack spacing={2} className="mb-4"></Stack>
          <div className="flex gap-4">
            <Button
              variant="soft"
              color="neutral"
              onClick={handleRetake}
              startDecorator={<CameraIcon />}
            >
              {t('faceVerification.retake')}
            </Button>
            <Button
              variant="soft"
              color="warning"
              onClick={handleContinue}
              endDecorator={
                <ForwardIcon sx={{ rotate: isRTL ? '180deg' : '0deg' }} />
              }
            >
              {t('faceVerification.continueAnyway')}
            </Button>
          </div>
        </div>
      )}

      {/* Switch Camera Button */}
      {isCameraOpen && (
        <div className="mt-4">
          <Button
            variant="soft"
            onClick={switchCamera}
            startDecorator={<FlipCameraIosIcon />}
          >
            {t('faceVerification.switchCamera', {
              mode:
                facingMode === 'user'
                  ? t('faceVerification.back')
                  : t('faceVerification.front'),
            })}
          </Button>
        </div>
      )}

      {/* Warning Dialog */}
      <Modal
        open={showWarningDialog}
        onClose={() => setShowWarningDialog(false)}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningIcon />
            {t('faceVerification.warningTitle')}
          </DialogTitle>
          <DialogContent>{t('faceVerification.warningMessage')}</DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="danger"
              onClick={handleConfirmContinue}
            >
              {t('faceVerification.continueAnyway')}
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setShowWarningDialog(false)}
            >
              {t('cancel')}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </div>
  );
};

export default FaceVerificationPage;
